import { useEffect, useMemo, useState } from "react";
import { Search, X, Mail, MessageCircle, ExternalLink, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUSES = ["new", "replied", "resolved", "wontfix"];
const SLA_HOURS = 24;

function hoursSince(iso) {
    if (!iso) return 0;
    return (Date.now() - new Date(iso).getTime()) / 3_600_000;
}
function isOverdue(x) {
    return x.status === "new" && hoursSince(x.created_at) > SLA_HOURS;
}

const STATUS_COLORS = {
    new: "text-[#0077b6] border-[#00aeef]/40 bg-[#00aeef]/10",
    replied: "text-amber-700 border-amber-400/40 bg-amber-400/10",
    resolved: "text-emerald-700 border-emerald-400/40 bg-emerald-400/10",
    wontfix: "text-neutral-600 border-neutral-300 bg-neutral-100",
};

export default function AdminHandoffs() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        api.get("/admin/chat/handoffs")
            .then((r) => {
                if (!cancelled) {
                    setItems(r.data || []);
                    setLoading(false);
                }
            })
            .catch((e) => {
                if (!cancelled) {
                    setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const filtered = useMemo(() => {
        let list = items;
        if (filter !== "all") list = list.filter((x) => x.status === filter);
        const q = query.trim().toLowerCase();
        if (q) {
            list = list.filter(
                (x) =>
                    (x.email || "").toLowerCase().includes(q) ||
                    (x.name || "").toLowerCase().includes(q) ||
                    (x.note || "").toLowerCase().includes(q)
            );
        }
        return list;
    }, [items, filter, query]);

    const selected = useMemo(() => items.find((x) => x.id === selectedId), [items, selectedId]);

    const stats = useMemo(() => {
        const byStatus = { new: 0, replied: 0, resolved: 0, wontfix: 0 };
        let overdue = 0;
        for (const x of items) {
            byStatus[x.status] = (byStatus[x.status] || 0) + 1;
            if (isOverdue(x)) overdue += 1;
        }
        return { byStatus, count: items.length, overdue };
    }, [items]);

    const changeStatus = async (id, next) => {
        setSavingId(id);
        try {
            const { data } = await api.patch(`/admin/chat/handoffs/${id}`, { status: next });
            setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...data } : x)));
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div data-testid="admin-handoffs">
            <div className="mb-6 sm:mb-8">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-2">
                    // Ask-a-human inbox
                </div>
                <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tighter">
                    Chat handoffs
                </h1>
                <p className="text-neutral-500 text-sm mt-2">
                    Visitors who escalated from the AI chat — reply from your inbox, then mark resolved here.
                </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="handoffs-kpis">
                <Kpi label="Total" value={stats.count} testid="kpi-total" />
                <Kpi label="New" value={stats.byStatus.new} testid="kpi-new" accent />
                <Kpi label="Overdue >24h" value={stats.overdue} testid="kpi-overdue" warn={stats.overdue > 0} />
                <Kpi label="Replied" value={stats.byStatus.replied} testid="kpi-replied" />
                <Kpi label="Resolved" value={stats.byStatus.resolved} testid="kpi-resolved" />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="flex flex-wrap gap-2">
                    {["all", ...STATUSES].map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilter(s)}
                            className={`text-[10px] uppercase tracking-[0.22em] px-3 py-2 rounded-lg border transition-colors ${
                                filter === s
                                    ? "border-[#0089c8] text-[#0077b6] bg-[#00aeef]/10"
                                    : "border-neutral-200 text-neutral-500 hover:text-neutral-900"
                            }`}
                            data-testid={`handoffs-filter-${s}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
                <div className="relative sm:ml-auto sm:w-72">
                    <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search email, name, note…"
                        className="w-full bg-white border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg pl-9 pr-3 py-2.5 text-sm text-neutral-900"
                        data-testid="handoffs-search"
                    />
                </div>
            </div>

            {error && (
                <div className="mb-4 border border-red-500/40 bg-red-50 text-red-700 text-xs p-3 rounded-lg">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="text-neutral-500 text-sm">Loading…</div>
            ) : filtered.length === 0 ? (
                <div className="border border-neutral-200 rounded-xl p-12 text-center text-neutral-500 text-sm" data-testid="handoffs-empty">
                    No handoff requests match this filter yet.
                </div>
            ) : (
                <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white">
                    <div className="hidden md:grid grid-cols-[1.4fr_1.6fr_1fr_0.8fr] gap-3 px-5 py-3 border-b border-neutral-200 bg-neutral-50 text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                        <div>Contact</div>
                        <div>Note</div>
                        <div>Status</div>
                        <div className="text-right">Received</div>
                    </div>
                    <div className="divide-y divide-neutral-200">
                        {filtered.map((x) => (
                            <button
                                type="button"
                                key={x.id}
                                onClick={() => setSelectedId(x.id)}
                                className="w-full text-left grid grid-cols-1 md:grid-cols-[1.4fr_1.6fr_1fr_0.8fr] gap-2 md:gap-3 px-5 py-4 hover:bg-neutral-50 transition-colors"
                                data-testid={`handoff-row-${x.id}`}
                            >
                                <div>
                                    <div className="font-semibold text-neutral-900 text-sm truncate">
                                        {x.name || "—"}
                                    </div>
                                    <div className="text-xs text-neutral-500 mt-0.5 truncate">{x.email}</div>
                                </div>
                                <div className="text-sm text-neutral-700 truncate">{x.note || <span className="text-neutral-400">(no note)</span>}</div>
                                <div>
                                    <span
                                        className={`inline-block text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded border ${
                                            STATUS_COLORS[x.status] || STATUS_COLORS.new
                                        }`}
                                    >
                                        {x.status}
                                    </span>
                                    {isOverdue(x) && (
                                        <span
                                            className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded border border-amber-400 bg-amber-100 text-amber-800"
                                            data-testid={`handoff-overdue-${x.id}`}
                                            title={`Waiting ${Math.round(hoursSince(x.created_at))}h — SLA is ${SLA_HOURS}h`}
                                        >
                                            <AlertTriangle className="w-3 h-3" />
                                            {Math.round(hoursSince(x.created_at))}h
                                        </span>
                                    )}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 md:text-right">
                                    {new Date(x.created_at).toLocaleDateString()}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {selected && (
                <div className="fixed inset-0 z-50 flex" data-testid="handoff-detail-drawer">
                    <button
                        type="button"
                        onClick={() => setSelectedId(null)}
                        aria-label="Close"
                        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
                    />
                    <div className="relative ml-auto w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto">
                        <div className="sticky top-0 z-10 bg-white border-b border-neutral-200 px-5 sm:px-8 py-4 flex items-start justify-between gap-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6]">// Handoff detail</div>
                                <div className="font-display text-xl sm:text-2xl font-bold text-neutral-900 truncate">
                                    {selected.name || selected.email}
                                </div>
                                <div className="text-xs text-neutral-500 mt-0.5">
                                    {selected.email} · {new Date(selected.created_at).toLocaleString()}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setSelectedId(null)}
                                className="p-2 border border-neutral-200 rounded-lg hover:border-[#0089c8]"
                                data-testid="handoff-detail-close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="px-5 sm:px-8 py-6 space-y-6">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2">Status</div>
                                <div className="flex flex-wrap gap-2">
                                    {STATUSES.map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            disabled={savingId === selected.id}
                                            onClick={() => changeStatus(selected.id, s)}
                                            className={`text-[10px] uppercase tracking-[0.22em] px-3 py-2 rounded-lg border transition-colors disabled:opacity-50 ${
                                                selected.status === s
                                                    ? STATUS_COLORS[s]
                                                    : "border-neutral-200 text-neutral-500 hover:text-neutral-900"
                                            }`}
                                            data-testid={`handoff-status-${s}`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="border border-neutral-200 rounded-lg p-4">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2">Reply by email</div>
                                <a
                                    href={`mailto:${selected.email}?subject=${encodeURIComponent(
                                        "Re: your Nexus Peptides chat"
                                    )}`}
                                    className="inline-flex items-center gap-2 text-[#0077b6] hover:underline text-sm"
                                    data-testid="handoff-reply-email"
                                >
                                    <Mail className="w-4 h-4" /> {selected.email}
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                            </div>

                            {selected.note && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2">Visitor's note</div>
                                    <div className="border border-neutral-200 rounded-lg p-4 text-sm text-neutral-800 whitespace-pre-wrap">
                                        {selected.note}
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2 flex items-center gap-2">
                                    <MessageCircle className="w-3 h-3" /> Chat transcript
                                </div>
                                <div className="border border-neutral-200 rounded-lg overflow-hidden">
                                    <div className="divide-y divide-neutral-200 max-h-[420px] overflow-y-auto">
                                        {(selected.transcript || []).length === 0 ? (
                                            <div className="p-4 text-xs text-neutral-500">(No messages captured.)</div>
                                        ) : (
                                            (selected.transcript || []).map((m, i) => (
                                                <div key={`${m.role}-${i}`} className="px-4 py-3 text-sm" data-testid={`handoff-msg-${i}`}>
                                                    <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-400 mb-1">
                                                        {m.role}
                                                    </div>
                                                    <div className="whitespace-pre-wrap text-neutral-800">{m.content}</div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Kpi({ label, value, testid, accent, warn }) {
    return (
        <div
            className={`border rounded-xl px-4 py-3 ${
                warn
                    ? "border-amber-400 bg-amber-50"
                    : accent
                    ? "border-[#00aeef]/40 bg-[#00aeef]/[0.06]"
                    : "border-neutral-200 bg-white"
            }`}
            data-testid={testid}
        >
            <div className={`text-[10px] uppercase tracking-[0.22em] ${warn ? "text-amber-700" : "text-neutral-500"}`}>{label}</div>
            <div
                className={`font-display font-bold text-2xl mt-1 ${
                    warn ? "text-amber-700" : accent ? "text-[#0077b6]" : "text-neutral-900"
                }`}
            >
                {value}
            </div>
        </div>
    );
}
